/**
 * useTourMascot
 *
 * Shared mascot state, asset loading and choreography for the universal tour.
 *
 * Mascot assets are cached in localStorage (24h TTL) so that the mascot
 * image appears immediately on subsequent loads even if the API is slow
 * or temporarily unavailable. On first load, a 5-second fetch timeout
 * prevents the UI from hanging indefinitely on gateway errors.
 */

'use client';

import { useEffect, useMemo, useState } from 'react';
import type { TourMascotConfig, TourStep } from '@/config/guided-tours/types';
import {
  buildMascotChoreography,
  getIndexChoreography,
  getCelebrationChoreography,
  type MascotChoreography,
  type MascotDockPosition,
} from '@/components/training/guided-tour/utils/mascotChoreography';

export interface MascotAsset {
  asset_key?: string | null;
  display_name?: string | null;
  file_url?: string | null;
  use_case?: string | null;
  position?: string | null;
  pointing_direction?: string | null;
}

interface UseTourMascotOptions {
  currentStep: TourStep | null;
  currentStepIndex: number;
  isOpen: boolean;
  isIndexMode?: boolean;
  isCelebration?: boolean;
  mascotConfig?: TourMascotConfig | null;
}

const MASCOT_CACHE_KEY = 'ta_mascot_assets_cache';
const MASCOT_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const MASCOT_FETCH_TIMEOUT_MS = 5000; // 5 seconds

interface MascotCacheEntry {
  assets: MascotAsset[];
  cachedAt: number;
}

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizeMascotAsset(asset: MascotAsset | null | undefined): MascotAsset | null {
  const fileUrl = hasText(asset?.file_url) ? asset.file_url.trim() : '';
  if (!fileUrl) {
    return null;
  }

  return {
    asset_key: hasText(asset?.asset_key) ? asset.asset_key.trim() : 'mascot-guide',
    display_name: hasText(asset?.display_name) ? asset.display_name.trim() : 'Mascot guide',
    file_url: fileUrl,
    use_case: hasText(asset?.use_case) ? asset.use_case.trim() : '',
    position: hasText(asset?.position) ? asset.position.trim() : '',
    pointing_direction: hasText(asset?.pointing_direction) ? asset.pointing_direction.trim() : null,
  };
}

function loadCachedAssets(): MascotAsset[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(MASCOT_CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as MascotCacheEntry;
    if (!entry?.assets || !Array.isArray(entry.assets) || typeof entry.cachedAt !== 'number') {
      return null;
    }
    if (Date.now() - entry.cachedAt > MASCOT_CACHE_TTL_MS) {
      return null;
    }
    return entry.assets.map(normalizeMascotAsset).filter(Boolean) as MascotAsset[];
  } catch {
    return null;
  }
}

function saveCachedAssets(assets: MascotAsset[]): void {
  if (typeof window === 'undefined') return;
  try {
    const entry: MascotCacheEntry = { assets, cachedAt: Date.now() };
    window.localStorage.setItem(MASCOT_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage might be full or unavailable — non-critical
  }
}

export function useTourMascot({
  currentStep,
  currentStepIndex,
  isOpen,
  isIndexMode = false,
  isCelebration = false,
  mascotConfig,
}: UseTourMascotOptions) {
  // Initialize from cache so the mascot appears immediately on repeat loads.
  const [assets, setAssets] = useState<MascotAsset[]>(() => {
    if (!isOpen) return [];
    const cached = loadCachedAssets();
    return cached ?? [];
  });
  const [assetsLoading, setAssetsLoading] = useState(false);

  const choreography: MascotChoreography = useMemo(() => {
    if (isCelebration) {
      return getCelebrationChoreography(mascotConfig ?? null);
    }

    if (isIndexMode || !currentStep) {
      return getIndexChoreography(mascotConfig ?? null);
    }

    return buildMascotChoreography(currentStep, currentStepIndex, mascotConfig ?? null);
  }, [currentStep, currentStepIndex, isCelebration, isIndexMode, mascotConfig]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const endpoint = mascotConfig?.api_endpoint;
    if (!endpoint) {
      return;
    }

    let cancelled = false;

    const loadAssets = async () => {
      setAssetsLoading(true);
      try {
        // Fetch with a timeout to avoid hanging on gateway errors (504s).
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), MASCOT_FETCH_TIMEOUT_MS);

        const response = await fetch(endpoint, { signal: controller.signal });
        window.clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Failed to load mascot assets: ${response.statusText}`);
        }

        const data = await response.json();
        const assetsArray = Array.isArray(data?.data) ? data.data : Array.isArray(data?.assets) ? data.assets : [];
        const normalized = assetsArray.map(normalizeMascotAsset).filter(Boolean) as MascotAsset[];

        if (!cancelled && normalized.length > 0) {
          setAssets(normalized);
          saveCachedAssets(normalized);
        }
      } catch (err) {
        console.warn('[useTourMascot] Failed to load mascot assets:', err);
        // If the fetch fails, try to use cached assets as a fallback.
        if (!cancelled) {
          const cached = loadCachedAssets();
          if (cached && cached.length > 0) {
            setAssets(cached);
          }
        }
      } finally {
        if (!cancelled) {
          setAssetsLoading(false);
        }
      }
    };

    loadAssets();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mascotConfig?.api_endpoint]);

  const currentPoseAsset = useMemo(() => {
    const pose = choreography.preferredPose;
    const match = assets.find((asset) => {
      const assetKey = hasText(asset.asset_key) ? asset.asset_key.trim() : '';
      return assetKey === pose;
    });

    return normalizeMascotAsset(match) ?? null;
  }, [assets, choreography.preferredPose]);

  const defaultPoseUrl = useMemo(() => {
    const pose = choreography.preferredPose;
    const fallbackKey = mascotConfig?.default_poses?.[pose];
    if (!hasText(fallbackKey)) {
      return undefined;
    }
    // Look up the fallback pose in loaded assets by asset_key
    const fallbackAsset = assets.find((asset) => {
      const assetKey = hasText(asset.asset_key) ? asset.asset_key.trim() : '';
      return assetKey === fallbackKey;
    });
    return fallbackAsset?.file_url ?? undefined;
  }, [choreography.preferredPose, mascotConfig?.default_poses, assets]);

  return {
    choreography,
    assets,
    assetsLoading,
    currentPoseAsset,
    defaultPoseUrl,
    dockPosition: choreography.dockPosition as MascotDockPosition,
  };
}
